"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.updateQueueSettingsSchema = exports.QUEUE_SETTINGS_DEFAULTS = exports.DAYS_RESOLVED_TO_CLOSED_RANGE = exports.REPLY_TO_LOCAL_PART_PATTERN = exports.REPLY_TO_LOCAL_PART = exports.EMAIL_TEMPLATES = exports.EMAIL_TEMPLATE_KEYS = exports.CLOSURE_EMAIL_PLACEHOLDERS = exports.ASSIGNMENT_EMAIL_PLACEHOLDERS = void 0;
exports.unknownPlaceholders = unknownPlaceholders;
exports.unknownPlaceholdersMessage = unknownPlaceholdersMessage;
exports.buildSenderEmail = buildSenderEmail;
exports.buildReplyToPattern = buildReplyToPattern;
const zod_1 = require("zod");
/**
 * Impostazioni di coda, casella mittente e testi delle email automatiche (PRD-004 US-6, story #10).
 * Un solo record per istanza: le regole vengono lette al momento dell'uso, quindi valgono per i
 * ticket e le email successivi alla modifica, mai retroattivamente (DEC-002 regola 4).
 */
/** Segnaposto ammessi nella notifica di assegnazione (al collega assegnato). */
exports.ASSIGNMENT_EMAIL_PLACEHOLDERS = ['{codice}', '{committente}', '{priorità}'];
/** Segnaposto ammessi nell'email di chiusura (a chi ha segnalato). */
exports.CLOSURE_EMAIL_PLACEHOLDERS = ['{codice}', '{descrizione}'];
/** Chiavi dei template di email automatiche. */
exports.EMAIL_TEMPLATE_KEYS = ['assignment', 'closure'];
/**
 * I template di email automatiche, nell'ordine in cui la schermata li mostra. Contratto per chi
 * li renderizza (#4 assegnazione, #6 chiusura): **sostituzione testuale** di ogni `{x}` con il
 * valore corrispondente, nessuna valutazione di espressioni; i valori vanno sottoposti a escape
 * HTML se l'email ha una parte HTML. I segnaposto non ammessi non arrivano qui: lo schema li
 * rifiuta al salvataggio.
 */
exports.EMAIL_TEMPLATES = [
    {
        key: 'assignment',
        label: 'Notifica di assegnazione',
        recipient: 'al collega assegnato',
        subjectField: 'assignmentSubject',
        bodyField: 'assignmentBody',
        placeholders: exports.ASSIGNMENT_EMAIL_PLACEHOLDERS,
    },
    {
        key: 'closure',
        label: 'Chiusura del ticket',
        recipient: 'a chi ha segnalato',
        subjectField: 'closureSubject',
        bodyField: 'closureBody',
        placeholders: exports.CLOSURE_EMAIL_PLACEHOLDERS,
    },
];
/** Local part riservata alle repliche (ADR-003): la casella mittente non può usarla. */
exports.REPLY_TO_LOCAL_PART = 'ticket';
/** Local part degli indirizzi `Reply-To` per ticket (ADR-003): `ticket+{codice}@dominio`. */
exports.REPLY_TO_LOCAL_PART_PATTERN = `${exports.REPLY_TO_LOCAL_PART}+{codice}`;
/** Intervallo ammesso per i giorni da `Risolto` a `Chiuso` (DEC-002 regola 4). */
exports.DAYS_RESOLVED_TO_CLOSED_RANGE = { min: 1, max: 365 };
/** Valori con cui l'istanza parte (riga seminata dalla migrazione). */
exports.QUEUE_SETTINGS_DEFAULTS = {
    senderName: 'Assistenza Easy Health',
    senderLocalPart: 'assistenza',
    assignmentSubject: '[{codice}] Ticket assegnato a te',
    assignmentBody: 'Ti è stato assegnato il ticket {codice} ({committente}), priorità {priorità}.\n\nApri il ticket in console per leggere la segnalazione e il contesto raccolto.',
    closureSubject: '[{codice}] La tua segnalazione è risolta',
    closureBody: 'La segnalazione {codice} è risolta.\n\n{descrizione}\n\nSe il problema si ripresenta, rispondi a questa email.',
    requirePriorityBeforeReply: true,
    requireDescriptionOnClose: true,
    daysResolvedToClosed: 14,
};
const PLACEHOLDER_PATTERN = /\{[^{}]*\}/g;
/** Segnaposto fra graffe presenti nel testo ma non fra quelli ammessi, senza duplicati. */
function unknownPlaceholders(text, allowed) {
    const found = text.match(PLACEHOLDER_PATTERN) ?? [];
    return [...new Set(found.filter((placeholder) => !allowed.includes(placeholder)))];
}
/** Messaggio di errore per i segnaposto non ammessi, con l'elenco di quelli disponibili. */
function unknownPlaceholdersMessage(unknown, allowed) {
    const subject = unknown.length === 1 ? 'Segnaposto non ammesso' : 'Segnaposto non ammessi';
    return `${subject}: ${unknown.join(', ')}. Puoi usare ${allowed.join(', ')}.`;
}
/** Indirizzo mittente completo, dato il dominio dell'istanza. */
function buildSenderEmail(localPart, mailDomain) {
    return `${localPart}@${mailDomain}`;
}
/** Schema degli indirizzi `Reply-To` per ticket, dato il dominio dell'istanza. */
function buildReplyToPattern(mailDomain) {
    return `${exports.REPLY_TO_LOCAL_PART_PATTERN}@${mailDomain}`;
}
/** Nessun carattere di controllo (categoria Unicode Cc: CR, LF, NUL...): un header di email non può contenerne. */
const NO_CONTROL_CHARS = /^\P{Cc}+$/u;
const NO_CONTROL_CHARS_MESSAGE = 'Non può contenere a capo o caratteri di controllo';
/** Nel corpo gli a capo sono ammessi; il NUL no (troncherebbe il testo in molti sistemi). */
const hasNoNul = (value) => !value.includes('\0');
function templateText(kind, max, allowed) {
    const label = kind === 'oggetto' ? "L'oggetto" : 'Il testo';
    return (zod_1.z
        .string()
        .trim()
        // Forma NFC: `{priorità}` scritto con accento combinante (NFD) è lo stesso segnaposto.
        .normalize('NFC')
        .min(1, { error: `${label} è obbligatorio`, abort: true })
        .max(max, { error: `${label} è troppo lungo (massimo ${max} caratteri)` })
        .refine(kind === 'oggetto' ? (value) => NO_CONTROL_CHARS.test(value) : hasNoNul, {
        error: kind === 'oggetto' ? NO_CONTROL_CHARS_MESSAGE : 'Non può contenere caratteri di controllo',
    })
        .check((ctx) => {
        const unknown = unknownPlaceholders(ctx.value, allowed);
        if (unknown.length > 0) {
            ctx.issues.push({ code: 'custom', message: unknownPlaceholdersMessage(unknown, allowed), input: ctx.value });
        }
    }));
}
const SUBJECT_MAX = 200;
const BODY_MAX = 5000;
/** Corpo di `PUT /settings/queue`: il record completo, validato con le stesse regole dell'API. */
exports.updateQueueSettingsSchema = zod_1.z.object({
    /** Nome visualizzato del mittente («Assistenza Easy Health»). */
    senderName: zod_1.z
        .string()
        .trim()
        .min(1, { error: 'Il nome del mittente è obbligatorio', abort: true })
        .max(120, { error: 'Nome del mittente troppo lungo (massimo 120 caratteri)' })
        .regex(NO_CONTROL_CHARS, { error: NO_CONTROL_CHARS_MESSAGE })
        // Il nome finisce nel `From` fra virgolette, prima di `<indirizzo>`: questi caratteri lo romperebbero.
        .regex(/^[^<>"]+$/, { error: 'Non può contenere < > o virgolette' }),
    /** Parte prima della chiocciola: il dominio è fisso per istanza e non si modifica da qui. */
    senderLocalPart: zod_1.z
        .string()
        .trim()
        .toLowerCase()
        .min(1, { error: 'La casella è obbligatoria', abort: true })
        .max(64, { error: 'Casella troppo lunga (massimo 64 caratteri)' })
        // dot-atom (RFC 5322): niente punto iniziale, finale o doppio.
        .regex(/^[a-z0-9_+-]+(\.[a-z0-9_+-]+)*$/, {
        error: 'Usa solo lettere minuscole, numeri e i caratteri . _ + - (il punto non all’inizio, alla fine o doppio)',
    })
        .refine((value) => value !== exports.REPLY_TO_LOCAL_PART && !value.startsWith(`${exports.REPLY_TO_LOCAL_PART}+`), {
        error: 'La casella «ticket» è riservata alle repliche',
    }),
    assignmentSubject: templateText('oggetto', SUBJECT_MAX, exports.ASSIGNMENT_EMAIL_PLACEHOLDERS),
    assignmentBody: templateText('testo', BODY_MAX, exports.ASSIGNMENT_EMAIL_PLACEHOLDERS),
    closureSubject: templateText('oggetto', SUBJECT_MAX, exports.CLOSURE_EMAIL_PLACEHOLDERS),
    closureBody: templateText('testo', BODY_MAX, exports.CLOSURE_EMAIL_PLACEHOLDERS),
    /** Blocca la risposta su un ticket senza priorità (PRD-004 US-2). */
    requirePriorityBeforeReply: zod_1.z.boolean({ error: 'Valore non valido' }),
    /** Richiede la descrizione della correzione con «Invia e chiudi» (PRD-004 US-5). */
    requireDescriptionOnClose: zod_1.z.boolean({ error: 'Valore non valido' }),
    /** Giorni senza replica dopo i quali un ticket `Risolto` passa a `Chiuso` (DEC-002). */
    daysResolvedToClosed: zod_1.z
        .number({ error: 'Inserisci un numero di giorni' })
        .int({ error: 'Inserisci un numero intero di giorni' })
        .min(exports.DAYS_RESOLVED_TO_CLOSED_RANGE.min, {
        error: `Almeno ${exports.DAYS_RESOLVED_TO_CLOSED_RANGE.min} giorno`,
    })
        .max(exports.DAYS_RESOLVED_TO_CLOSED_RANGE.max, {
        error: `Al massimo ${exports.DAYS_RESOLVED_TO_CLOSED_RANGE.max} giorni`,
    }),
});
//# sourceMappingURL=queue-settings.js.map